import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { useTranslation } from '../../i18n';
import { useConfig } from '../../hooks/useConfig';
import { openWhatsApp } from '../../services/whatsapp';

const PlatformsTab = () => {
  const { t } = useTranslation();
  const { config: appConfig } = useConfig();
  const [whatsappVerified, setWhatsappVerified] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastAttempt, setLastAttempt] = useState(0);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      const response = await apiClient.get('/configuration');
      if (response.data.whatsapp_phone) {
        setWhatsappVerified(true);
        setUserPhone(response.data.whatsapp_phone);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    }
  };

  const startWhatsAppConversation = async () => {
    // Rate limiting: prevent multiple clicks within 10 seconds
    const now = Date.now();
    if (now - lastAttempt < 10000) {
      setError(t('pleasewait') || 'Please wait a few seconds before trying again');
      return;
    }
    
    setLoading(true);
    setError('');
    setLastAttempt(now);

    try {
      const botPhone = appConfig?.whatsapp?.botPhoneNumber || '5519994823795';
      
      const response = await apiClient.post('/whatsapp/generate-verification', {
        phone_number: `+${botPhone}`
      });
      
      const code = response.data.code;
      const message = t('whatsappVerificationMessage').replace('{code}', code);
      
      // Open WhatsApp with verification message
      openWhatsApp(botPhone, message);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t('tooManyRequests') || 'Too many requests. Please wait a few minutes and try again.');
      } else {
        setError(t('failedToGenerateCode') || 'Failed to generate verification code');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>{t('platformConnections')}</h2>
        <p>{t('platformConnectionsDesc')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="platform-card">
        <div className="platform-header">
          <div className="platform-icon">💬</div>
          <div>
            <h3>WhatsApp</h3>
            <p>{t('whatsappConnectDesc')}</p>
          </div>
          {whatsappVerified && (
            <span className="badge badge-success">{t('connected')}</span>
          )}
        </div>

        {/* Bot Number - Always visible */}
        <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            {t('botNumber')}
          </div>
          <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
            +{appConfig?.whatsapp?.botPhoneNumber || '5519994823795'}
          </div>
        </div>

        {/* User Number - Only when verified */}
        {whatsappVerified && userPhone ? (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #86efac' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                  {t('yourNumber')}
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#166534' }}>
                  +{userPhone}
                </div>
              </div>
              <button
                onClick={startWhatsAppConversation}
                className="btn btn-outline"
                disabled={loading}
                style={{ flexShrink: 0 }}
              >
                {loading ? t('opening') : t('changeNumber')}
              </button>
            </div>
          </div>
        ) : (
          /* Not verified - Show instructions and connect button */
          <>
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem', border: '1px solid #93c5fd' }}>
              <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
                💡 {t('clickStartConversationToVerify')}
              </p>
            </div>
            <button
              onClick={startWhatsAppConversation}
              className="btn btn-primary"
              disabled={loading}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              💬 {loading ? t('opening') : t('startConversation')}
            </button>
          </>
        )}

        {whatsappVerified && (
          <div className="verified-info" style={{ marginTop: '1rem' }}>
            <p>✓ {t('whatsappConnectedReady')}</p>
            <p className="text-muted">{t('sendMessageToStart')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformsTab;
